import { useMemo, useState } from 'react'

import { Autocomplete, cn, Description, EmptyState, Label, ListBox, SearchField, Spinner } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { useArticles } from '@/hooks/useArticles'

import { Item } from './Item'

interface ArticleAutocompleteProps {
  label?: string;
  description?: string;
  placeholder?: string;
  onSelectionChange?: (article: string | null) => void;
  className?: string;
  selectedKey?: string;
  isInvalid?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
  allowCustomValue?: boolean;
}

export default ({
  label,
  description,
  placeholder,
  onSelectionChange,
  className,
  selectedKey,
  isInvalid,
  isRequired,
  errorMessage,
  allowCustomValue = true,
}: ArticleAutocompleteProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { data, isLoading } = useArticles(search)

  // Map article names and prepend the search term if it's new/custom
  const items = useMemo(() => {
    const list = data.map(name => ({ id: name, name }))
    const trimmedSearch = search.trim()
    if (
      allowCustomValue &&
      trimmedSearch &&
      !data.some(name => name.toLowerCase() === trimmedSearch.toLowerCase())
    ) {
      list.unshift({
        id: trimmedSearch,
        name: trimmedSearch, // The internal selection value will be the typed term
      })
    }
    return list
  }, [data, search, allowCustomValue])

  const finalPlaceholder = placeholder || t('article_autocomplete.placeholder', 'Введите наименование детали')

  return (
    <Autocomplete
      items={items as any}
      allowsEmptyCollection
      variant={'secondary'}
      className={className}
      selectedKey={selectedKey}
      onSelectionChange={key => {
        onSelectionChange?.(key as string | null)
      }}
      isInvalid={isInvalid}
      isRequired={isRequired}
    >
      {label && <Label>{label}</Label>}
      <Autocomplete.Trigger className="w-full">
        <Autocomplete.Value className="w-full">
          {({ isPlaceholder, state }) => {
            if (isPlaceholder) return finalPlaceholder
            const selKey = state.selectedKey as string | undefined
            return selKey || null
          }}
        </Autocomplete.Value>
        {selectedKey && selectedKey !== 'none' && (
          <div onClick={e => {
            e.stopPropagation()
            onSelectionChange?.(null)
          }}
          >
            <Autocomplete.ClearButton />
          </div>
        )}
        <Autocomplete.Indicator />
      </Autocomplete.Trigger>
      {description && <Description>{description}</Description>}
      {errorMessage && isInvalid && <span className="text-xs text-danger">{errorMessage}</span>}
      <Autocomplete.Popover>
        <Autocomplete.Filter>
          <SearchField
            onChange={val => {
              setSearch(val)
            }}
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={t('article_autocomplete.search_placeholder', 'Поиск детали...')} autoFocus />
              <Spinner
                size="sm"
                className={cn("absolute top-1/2 right-2 -translate-y-1/2", {
                  "pointer-events-none opacity-0": !isLoading,
                })}
              />
            </SearchField.Group>
          </SearchField>
          <ListBox
            items={items}
            renderEmptyState={() => <EmptyState>{t('article_autocomplete.no_results', 'Нет результатов')}</EmptyState>}
          >
            {(item: { id: string; name: string }) => {
              const isCustom = (trimmed: string) => trimmed && !data.some(n => n.toLowerCase() === trimmed.toLowerCase())
              const trimmedSearch = search.trim()
              const labelText = (allowCustomValue && item.id === trimmedSearch && isCustom(trimmedSearch))
                ? `Создать: "${item.name}"`
                : item.name

              return (
                <ListBox.Item key={item.id} id={item.id} textValue={item.name}>
                  <Item item={labelText} />
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              )
            }}
          </ListBox>
        </Autocomplete.Filter>
      </Autocomplete.Popover>
    </Autocomplete>
  )
}
